import React, { useState } from 'react';
import {
    Building2,
    ChevronLeft,
    AlertCircle,
    ShieldCheck,
    CheckCircle2,
    RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/shared/lib/utils';
import { useKYC } from '@/shared/hooks/useKYC';

import { useAuth } from '@/shared/hooks/useAuth';

const AccountVerificationPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { submitApplication } = useKYC(user?.uid);
    const [bankName, setBankName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [accountHolder, setAccountHolder] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        if (!bankName || !accountNumber || !accountHolder) return;

        setIsSubmitting(true);
        const result = await submitApplication('ACCOUNT', {
            bankName,
            accountNumber,
            accountHolder
        });

        if (result.success) {
            alert('인증 요청이 완료되었습니다. 관리자 승인 후 반영됩니다.');
            navigate('/verification');
        } else {
            alert('인증 요청 중 오류가 발생했습니다. 다시 시도해주세요.');
        }
        setIsSubmitting(false);
    };

    return (
        <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in duration-700">
            <button
                onClick={() => navigate('/verification')}
                className="flex items-center gap-2 text-slate-500 hover:text-white transition-colors text-sm font-bold group"
            >
                <ChevronLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" /> 뒤로가기
            </button>

            <div className="space-y-2">
                <h1 className="text-3xl font-black text-white flex items-center gap-3">
                    <Building2 className="h-8 w-8 text-blue-500" /> 계좌 실명 인증
                </h1>
                <p className="text-slate-500 font-bold text-sm">본인 명의의 은행 계좌 정보를 입력해주세요.</p>
            </div>

            <div className="rounded-[2.5rem] bg-[#0f0f12] border border-white/5 p-8 shadow-2xl space-y-8">
                <div className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1">은행명</label>
                        <input
                            type="text"
                            placeholder="예: 국민은행, 신한은행"
                            value={bankName}
                            onChange={(e) => setBankName(e.target.value)}
                            className="w-full h-14 bg-white/5 border border-white/5 rounded-2xl px-6 text-sm font-bold text-white outline-none focus:border-blue-500/50 transition-all"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1">계좌번호</label>
                        <input
                            type="text"
                            placeholder="하이픈(-) 없이 입력"
                            value={accountNumber}
                            onChange={(e) => setAccountNumber(e.target.value)}
                            className="w-full h-14 bg-white/5 border border-white/5 rounded-2xl px-6 text-sm font-bold text-white outline-none focus:border-blue-500/50 transition-all"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1">예금주명</label>
                        <input
                            type="text"
                            placeholder="본인 성함"
                            value={accountHolder}
                            onChange={(e) => setAccountHolder(e.target.value)}
                            className="w-full h-14 bg-white/5 border border-white/5 rounded-2xl px-6 text-sm font-bold text-white outline-none focus:border-blue-500/50 transition-all"
                        />
                    </div>
                </div>

                <div className="p-6 rounded-3xl bg-amber-500/5 border border-amber-500/10 flex items-start gap-4">
                    <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                        <p className="text-xs font-black text-amber-500">유의사항</p>
                        <p className="text-[10px] font-bold text-slate-500 leading-relaxed">
                            반드시 본인 명의의 계좌를 입력해야 하며, 타인 명의 계좌 사용 시 서비스 이용이 제한될 수 있습니다.
                            인증 결과는 영업일 기준 최대 24시간 이내에 처리됩니다.
                        </p>
                    </div>
                </div>

                <button
                    onClick={handleSubmit}
                    disabled={!bankName || !accountNumber || !accountHolder || isSubmitting}
                    className="w-full h-16 rounded-2xl bg-blue-600 text-white font-black shadow-xl shadow-blue-600/20 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all uppercase tracking-widest text-sm flex items-center justify-center gap-3"
                >
                    {isSubmitting ? <RefreshCw className="h-5 w-5 animate-spin" /> : "인증 요청하기"}
                </button>
            </div>

            <div className="flex items-center justify-center gap-2 text-slate-600">
                <ShieldCheck className="h-4 w-4" />
                <span className="text-[10px] font-black uppercase tracking-widest">End-to-End Encrypted & Secure</span>
            </div>
        </div>
    );
};

export default AccountVerificationPage;
